import { useRef, useState, useEffect, ReactNode, ComponentType, Suspense, lazy } from "react";

interface LazyOnVisibleProps {
  /** The lazy-loaded component to render when visible */
  component: () => Promise<{ default: ComponentType<any> }>;
  /** Fallback to show while loading */
  fallback: ReactNode;
  /** Props to pass to the component */
  componentProps?: Record<string, any>;
  /** Root margin for intersection observer (how early to start loading) */
  rootMargin?: string;
  /** Minimum height for placeholder to prevent layout shift */
  minHeight?: string;
  /** Class name for the container */
  className?: string;
}

/**
 * LazyOnVisible - Only loads and renders a component when it's near the viewport.
 * This prevents below-fold heavy components from competing with initial page load.
 */
export function LazyOnVisible({
  component,
  fallback,
  componentProps = {},
  rootMargin = "400px",
  minHeight,
  className,
}: LazyOnVisibleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [LazyComponent, setLazyComponent] = useState<ComponentType<any> | null>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Use IntersectionObserver to detect when element is near viewport
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      {
        rootMargin, // Start loading before element is visible
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin]);

  // Load the component when shouldLoad becomes true
  useEffect(() => {
    if (!shouldLoad || LazyComponent) return;

    component().then((module) => {
      setLazyComponent(() => module.default);
    }).catch((error) => {
      console.error("Failed to load lazy component:", error);
    });
  }, [shouldLoad, component, LazyComponent]);

  return (
    <div ref={containerRef} className={className} style={minHeight ? { minHeight } : undefined}>
      {LazyComponent ? (
        <Suspense fallback={fallback}>
          <LazyComponent {...componentProps} />
        </Suspense>
      ) : (
        fallback
      )}
    </div>
  );
}

export default LazyOnVisible;
